import React from 'react';
import {FeedOriginalPost, FeedPost} from '../../../../services/socialFeedApi';
import {
  formatRelativeTime,
  getAssetUrl,
  getAuthorName,
} from '../utils';
import PostAuthorActionMenu from './PostAuthorActionMenu';
import PostAuthorAvatar from './PostAuthorAvatar';
import styles from '../Home.module.css';

const mediaOutlineIconStyle = {
  '--home-post-media-placeholder-icon-url': `url("${process.env.PUBLIC_URL}/icons/media-outline.svg")`,
} as React.CSSProperties;

type ReadonlyFeedPost = FeedOriginalPost | FeedPost;

interface FeedPostReadonlyPreviewProps {
  post: ReadonlyFeedPost | null;
  failedImages: Record<string, boolean>;
  onImageError: (postId: string, mediaIndex: number) => void;
  contextLabel?: string;
  unavailableTitle?: string;
  unavailableDescription?: string;
  currentUserId?: string | null;
  pendingFollowAuthorIds?: Record<string, boolean>;
  onToggleAuthorFollow?: (authorId: string, isFollowing: boolean) => void;
}

const FeedPostReadonlyPreview: React.FC<FeedPostReadonlyPreviewProps> = ({
  post,
  failedImages,
  onImageError,
  contextLabel,
  unavailableTitle = 'Original blog unavailable',
  unavailableDescription = 'This repost is still available, but the original blog can no longer be displayed.',
  currentUserId,
  pendingFollowAuthorIds,
  onToggleAuthorFollow,
}) => {
  if (!post) {
    return (
      <div className={styles.repostReferenceCard}>
        {contextLabel ? <p className={styles.repostReferenceLabel}>{contextLabel}</p> : null}
        <div className={styles.repostReferenceUnavailable}>
          <p className={styles.repostReferenceUnavailableTitle}>{unavailableTitle}</p>
          <p className={styles.repostReferenceUnavailableText}>{unavailableDescription}</p>
        </div>
      </div>
    );
  }

  const authorName = getAuthorName(post);
  const postMediaUrls = (post.mediaUrls || []).slice(0, 4);
  const authorId = post.authorId?._id || '';

  return (
    <div className={styles.repostReferenceCard}>
      {contextLabel ? <p className={styles.repostReferenceLabel}>{contextLabel}</p> : null}

      <div className={styles.postHeader}>
        <PostAuthorAvatar
          name={authorName}
          profilePicture={post.authorId?.profile?.profilePicture}
        />
        <div className={styles.postMeta}>
          <h4 className={styles.postAuthor}>
            <PostAuthorActionMenu
              author={post.authorId}
              authorName={authorName}
              currentUserId={currentUserId}
              isFollowPending={Boolean(authorId && pendingFollowAuthorIds?.[authorId])}
              onToggleFollow={onToggleAuthorFollow}
            />
          </h4>
          <p className={styles.postTime}>{formatRelativeTime(post.createdAt)}</p>
        </div>
      </div>

      {post.content ? (
        <p className={styles.postContent}>{post.content}</p>
      ) : null}

      {postMediaUrls.length > 0 ? (
        <div className={`${styles.postMediaGrid} ${styles[`postMediaGrid${postMediaUrls.length}`]}`.trim()}>
          {postMediaUrls.map((postMediaUrl, mediaIndex) => {
            const imageUrl = getAssetUrl(postMediaUrl);
            const hasFailedImage = Boolean(failedImages[`${post._id}:${mediaIndex}`]);
            const showImage = Boolean(imageUrl) && !hasFailedImage;

            return (
              <div key={`${post._id}-${mediaIndex}`} className={styles.postMediaItem}>
                {showImage ? (
                  <img
                    src={imageUrl}
                    alt={`${authorName}'s post ${mediaIndex + 1}`}
                    className={styles.postMediaImage}
                    onError={() => onImageError(post._id, mediaIndex)}
                  />
                ) : (
                  <div className={styles.postMediaPlaceholder} role="img" aria-label="Post media unavailable">
                    <span className={styles.postMediaPlaceholderIcon} style={mediaOutlineIconStyle} aria-hidden="true" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(FeedPostReadonlyPreview);
